import { useEffect, useMemo, useRef, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";
import { GRAPH_BASE, resolveUserByNameOrEmail } from "../api/graph";
import { isSharePointUrl, resolveImage } from "../api/featured";
import { bundledPhoto } from "../api/champions";
import type { Champion } from "../data/championsDirectory";

export type ChampionProfile = Champion & { photoUrl: string | null };

type GraphDetails = { jobTitle?: string | null; city?: string | null; state?: string | null; officeLocation?: string | null; mail?: string | null };
type Extra = Partial<Pick<ChampionProfile, "title" | "location" | "email" | "photoUrl">>;

/** Where a champion's photo comes from: what we hold for them, or a photo
 *  bundled with the app. SharePoint links still need resolving. */
const ownPhoto = (c: Champion): string | undefined => {
  const p = c.photo ?? bundledPhoto(c.name);
  if (!p) return undefined;
  return /^(blob:|https?:|\/)/.test(p) ? p : `/champions/${p}.jpg`;
};

const hasGaps = (c: Champion) => !ownPhoto(c) || !c.title || !c.location || !c.email;

/** A champion's title, location, email and photo from their Microsoft 365
 *  profile. Anything that can't be found is left blank. */
async function lookUp(token: string, c: Champion): Promise<Extra> {
  const user = await resolveUserByNameOrEmail(token, c.email ?? c.name);
  if (!user) return {};
  const headers = { Authorization: `Bearer ${token}` };
  const out: Extra = {};
  const res = await fetch(`${GRAPH_BASE}/users/${user.id}?$select=jobTitle,city,state,officeLocation,mail`, { headers });
  if (res.ok) {
    const d = (await res.json()) as GraphDetails;
    if (d.jobTitle) out.title = d.jobTitle;
    const place = [d.city, d.state].filter(Boolean).join(", ") || d.officeLocation;
    if (place) out.location = place;
    if (d.mail || user.mail) out.email = (d.mail || user.mail) ?? undefined;
  }
  if (!ownPhoto(c)) {
    const photo = await fetch(`${GRAPH_BASE}/users/${user.id}/photos/240x240/$value`, { headers });
    if (photo.ok) out.photoUrl = URL.createObjectURL(await photo.blob());
  }
  return out;
}

/** The champions, with gaps in what we hold about them (photo, title,
 *  location, email) filled in from Microsoft 365. What we hold always wins;
 *  the list shows straight away and fills in as lookups come back. */
export function useChampionDirectory(champions: Champion[]): ChampionProfile[] {
  const getToken = useGraphToken();
  const [extras, setExtras] = useState<Record<string, Extra>>({});
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const asked = useRef(new Set<string>());
  const blobs = useRef<string[]>([]);
  const mounted = useRef(true);

  const gapKey = champions.filter(hasGaps).map((c) => `${c.name}|${c.email ?? ""}`).join(";");
  const spPhotos = [...new Set(champions.map(ownPhoto).filter((p): p is string => !!p && isSharePointUrl(p)))];
  const spKey = spPhotos.join(";");

  // Fill gaps from Microsoft 365, once per person.
  useEffect(() => {
    const todo = champions.filter((c) => hasGaps(c) && !asked.current.has(`${c.name}|${c.email ?? ""}`));
    if (!todo.length) return;
    todo.forEach((c) => asked.current.add(`${c.name}|${c.email ?? ""}`));
    void (async () => {
      let token: string;
      try {
        token = await getToken();
      } catch {
        return;
      }
      await Promise.all(
        todo.map(async (c) => {
          try {
            const extra = await lookUp(token, c);
            if (extra.photoUrl) blobs.current.push(extra.photoUrl);
            if (mounted.current && Object.keys(extra).length) setExtras((prev) => ({ ...prev, [c.name]: extra }));
          } catch {
            /* a missing profile just leaves what we hold */
          }
        })
      );
    })();
    // Re-run only when the set of people with gaps changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapKey, getToken]);

  // Photos stored in SharePoint need the viewer's sign-in to load.
  useEffect(() => {
    const todo = spPhotos.filter((p) => !resolved[p]);
    if (!todo.length) return;
    void (async () => {
      let token: string;
      try {
        token = await getToken();
      } catch {
        return;
      }
      for (const url of todo) {
        try {
          const blob = await resolveImage(token, url);
          if (mounted.current) setResolved((prev) => ({ ...prev, [url]: blob }));
        } catch {
          /* falls back to initials */
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spKey, getToken]);

  useEffect(() => {
    mounted.current = true;
    const urls = blobs.current;
    return () => {
      mounted.current = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  return useMemo(
    () =>
      champions.map((c) => {
        const x = extras[c.name] ?? {};
        const own = ownPhoto(c);
        const photoUrl = own ? (isSharePointUrl(own) ? resolved[own] ?? null : own) : x.photoUrl ?? null;
        return {
          ...c,
          title: c.title || x.title,
          location: c.location || x.location,
          email: c.email || x.email,
          photoUrl,
        };
      }),
    [champions, extras, resolved]
  );
}
